// MobilePhotoQRModal.jsx — Sprint-83
// ─────────────────────────────────────────────────────────────────────
// Desktop modal that displays a QR pointing to /m/upload/:sessionId,
// polls the session every 1.5 s, and resolves with the uploaded image
// URL via `onPhotoReceived`. Shows a 15-minute countdown and a Copy-URL
// fallback for users without a QR-capable phone.
import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Copy, Smartphone, Check, RefreshCcw, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import api from "../services/api";
import { toast } from "sonner";

export default function MobilePhotoQRModal({ open, onClose, onPhotoReceived }) {
  const [session, setSession] = useState(null);     // { session_id, upload_url, expires_at }
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(15 * 60);
  const [status, setStatus] = useState("pending");  // pending | uploaded | expired
  const [copied, setCopied] = useState(false);
  const pollTimer = useRef(null);
  const tickTimer = useRef(null);
  const resolved = useRef(false);                   // guard against double-fire

  // ── Create session on open ──
  const createSession = async () => {
    setCreating(true);
    setCreateErr(null);
    setStatus("pending");
    resolved.current = false;
    try {
      const { data } = await api.post("/customer/photo-session");
      setSession(data);
      const exp = new Date(data.expires_at).getTime();
      const now = Date.now();
      setSecondsLeft(Math.max(0, Math.floor((exp - now) / 1000)));
    } catch (e) {
      setCreateErr(e?.response?.data?.detail?.message || e?.message || "Could not create photo session");
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    if (open) createSession();
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
      if (tickTimer.current) clearInterval(tickTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Poll session status every 1.5 s while pending ──
  useEffect(() => {
    if (!session?.session_id || status !== "pending") return;
    pollTimer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/customer/photo-session/${session.session_id}`);
        if (data.status === "uploaded" && !resolved.current) {
          resolved.current = true;
          setStatus("uploaded");
          clearInterval(pollTimer.current);
          // Pass the *absolute* image URL upstream so <img> can load it directly.
          const base = api.defaults.baseURL || "";
          const imgUrl = `${base}/customer/photo-session/${session.session_id}/image`;
          toast.success("Photo received from mobile");
          onPhotoReceived?.(imgUrl, session.session_id);
          // Auto-close after a short success state
          setTimeout(() => onClose?.(), 800);
        }
      } catch (e) {
        if (e?.response?.status === 410) {
          setStatus("expired");
          clearInterval(pollTimer.current);
        }
      }
    }, 1500);
    return () => clearInterval(pollTimer.current);
  }, [session?.session_id, status, onPhotoReceived, onClose]);

  // ── 1 s ticker for countdown ──
  useEffect(() => {
    if (!session?.expires_at) return;
    tickTimer.current = setInterval(() => {
      const left = Math.max(0, Math.floor((new Date(session.expires_at).getTime() - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) {
        setStatus("expired");
        clearInterval(pollTimer.current);
        clearInterval(tickTimer.current);
      }
    }, 1000);
    return () => clearInterval(tickTimer.current);
  }, [session?.expires_at]);

  const mm = useMemo(() => String(Math.floor(secondsLeft / 60)).padStart(2, "0"), [secondsLeft]);
  const ss = useMemo(() => String(secondsLeft % 60).padStart(2, "0"), [secondsLeft]);

  const copy = async () => {
    if (!session?.upload_url) return;
    try {
      await navigator.clipboard.writeText(session.upload_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="mobile-photo-qr-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-orange-500" />
            Take Photo from Mobile
          </DialogTitle>
          <DialogDescription>
            Scan this code with your phone to open the camera. The photo will appear here automatically.
          </DialogDescription>
        </DialogHeader>

        {creating && (
          <div className="flex flex-col items-center justify-center py-10" data-testid="qr-loading">
            <Loader2 className="h-7 w-7 text-orange-500 animate-spin mb-3" />
            <p className="text-sm text-slate-500">Generating secure link…</p>
          </div>
        )}

        {createErr && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700" data-testid="qr-error">
            <p className="font-medium mb-1">Could not start the photo session.</p>
            <p className="text-xs text-red-600/80">{createErr}</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={createSession} data-testid="qr-retry-btn">
              <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Retry
            </Button>
          </div>
        )}

        {session && !creating && status !== "expired" && (
          <div className="space-y-4">
            {/* QR card */}
            <div className="flex flex-col items-center bg-white rounded-2xl border border-slate-200 p-5">
              <div className="bg-white p-3 rounded-xl border border-slate-100" data-testid="qr-code-wrapper">
                <QRCodeSVG value={session.qr_payload} size={208} level="M" includeMargin={false} />
              </div>
              {status === "uploaded" ? (
                <p className="mt-4 text-sm font-semibold text-emerald-600 flex items-center gap-1.5" data-testid="qr-status-uploaded">
                  <Check className="h-4 w-4" /> Photo received — closing…
                </p>
              ) : (
                <p className="mt-4 text-xs text-slate-500 flex items-center gap-1.5" data-testid="qr-status-waiting">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Waiting for your phone… expires in <span className="font-mono font-semibold text-slate-700">{mm}:{ss}</span>
                </p>
              )}
            </div>

            {/* Fallback URL */}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Can't scan? Open this link on your phone:</p>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={session.upload_url}
                  className="flex-1 text-xs bg-white border border-slate-200 rounded-md px-2 py-1.5 font-mono text-slate-700 truncate"
                  data-testid="qr-fallback-url"
                  onClick={(e) => e.target.select()}
                />
                <Button size="sm" variant="outline" onClick={copy} data-testid="qr-copy-btn">
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
          </div>
        )}

        {status === "expired" && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800" data-testid="qr-expired">
            <p className="font-medium mb-1">This QR link expired.</p>
            <p className="text-xs">Photo sessions expire after 15 minutes. Click Retry to generate a new one.</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={createSession} data-testid="qr-renew-btn">
              <RefreshCcw className="h-3.5 w-3.5 mr-1" /> New QR code
            </Button>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="qr-close-btn">
            <X className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
